// Listing modes for the Modifications register — List / Process.
//
// Modes are ROUTES (PATTERNS.md §"Module weight"), not `useState`. This file
// holds ONLY the vocabulary, deliberately separated from the layout that
// renders it, mirroring src/ads-and-sbs/modes.ts — because this module is
// mounted TWO ways and both need the same segments, labels, storage key and
// validator:
//
//   1. standalone  /modifications/<mode>                    → ModificationsLayout
//   2. nested       /aircraft_templates/modifications/<mode>  → AircraftTemplatesLayout
//
// Also a leaf file (no React/MUI imports) so a synchronous import from
// AircraftTemplatesLayout.tsx does not pull ModificationsLayout's MUI graph
// into the eagerly-loaded aircraft-templates chunk (see F3 in the phase 2
// review — the same reason ads-and-sbs/modes.ts exists).

import type { ModeDescriptions } from "../components/BreadcrumbDropdownMenu";

/**
 * Mode segment -> breadcrumb / dropdown label. Keys ARE the child route paths
 * in index.tsx; adding one here without a matching <Route> gives a dropdown
 * entry that 404s against the module's own route table.
 *
 * SEGMENTS are Aaron's ("List" and "Process", 2026-08-24). The list-side label
 * was "Modification Directory" — the retired ViewModeHeaderSwitcher's string —
 * justified here as "the shape every converted module uses". That claim was
 * measured and is false: 8 of the 11 modules sharing this segment label it
 * "List", and this was one of three that did not. Aaron, on the same
 * deviation in flight-expenses: "why does it not say list like other modules".
 * The module title already supplies the domain word, which is why the majority
 * does not repeat it here.
 */
export const MODE_LABELS: { [segment: string]: string } = {
  list: "List",
  process: "Process",
};

/**
 * Mode segment -> the grey line under its label in the breadcrumb dropdown
 * (BP-85, H76: "ALL model switchers should have name and description the same
 * as demand"). Same keys as the label map; each read off what the mode's
 * component renders, not paraphrased from the label.
 */
export const MODE_DESCRIPTIONS: ModeDescriptions = {
  list: "Fleet STCs and mods",
  process: "Modification KPIs and mix",
};

/** Matches the retired switcher's default mode ("directory"). */
export const DEFAULT_MODE_SEGMENT = "list";

/**
 * Unchanged from the `useViewMode("modifications", …)` call this replaced.
 * That hook built its key as `${moduleKey}.viewMode` at runtime, so the
 * literal below must keep matching it or every stored mode resets on deploy.
 */
export const MODE_STORAGE_KEY = "modifications.viewMode";

/**
 * Values the RETIRED ViewModeHeaderSwitcher wrote under MODE_STORAGE_KEY,
 * mapped to their new segment. "directory" was that switcher's default mode.
 */
const LEGACY_SEGMENTS: { [legacy: string]: string } = {
  directory: "list",
  overview: "process",
};

/**
 * Is this a real mode segment? Uses hasOwnProperty, NOT `in` — `in` walks the
 * prototype chain, so "constructor" / "toString" / "valueOf" / "__proto__"
 * all test true against an object literal and would pass as valid modes.
 */
export const isModeSegment = (segment: string): boolean =>
  Object.prototype.hasOwnProperty.call(MODE_LABELS, segment);

const isLegacySegment = (segment: string): boolean =>
  Object.prototype.hasOwnProperty.call(LEGACY_SEGMENTS, segment);

/** Redirect target for a bare visit to the module root. */
export function readRememberedModeSegment(): string {
  if (typeof window === "undefined") return DEFAULT_MODE_SEGMENT;
  let stored: string | null = null;
  try {
    stored = window.localStorage.getItem(MODE_STORAGE_KEY);
  } catch {
    // Safari private mode / quota-blocked storage throws on access; a mode
    // redirect is not worth an unhandled exception.
    return DEFAULT_MODE_SEGMENT;
  }
  if (!stored) return DEFAULT_MODE_SEGMENT;
  if (isModeSegment(stored)) return stored;
  if (isLegacySegment(stored)) return LEGACY_SEGMENTS[stored];
  return DEFAULT_MODE_SEGMENT;
}

/**
 * Split a live pathname into the module base and the mode segment. Shared by
 * both layouts so the two mounts derive the base identically.
 */
export function splitModePath(pathname: string): {
  basePath: string;
  currentMode: string;
  onMode: boolean;
} {
  const segments = pathname.split("/").filter(Boolean);
  const lastSegment = segments[segments.length - 1] ?? "";
  const onMode = isModeSegment(lastSegment);
  const baseSegments = onMode ? segments.slice(0, -1) : segments;
  // Empty-check before prefixing the slash: a bare "/" + "" would make the
  // mode links "//list", which a browser resolves as a PROTOCOL-RELATIVE URL
  // (host "list") and navigates off-site.
  return {
    basePath: baseSegments.length ? "/" + baseSegments.join("/") : "",
    currentMode: onMode ? lastSegment : DEFAULT_MODE_SEGMENT,
    onMode,
  };
}
